import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { TransferModal } from "./TransferModal";
import { AdvancedSettingsModal } from "../../../components/modals/AdvancedSettingsModal";
import { MDBBtn, MDBInput, MDBTextArea } from "mdb-react-ui-kit";
import { CustomSelect } from "../../../components/CustomSelect";
import { TextInput } from "../../../components/core/inputs/TextInput";
import { TextAreaInput } from "../../../components/core/inputs/TextAreaInput";
import { Alert } from "../../../components/Alert";
import { ChangeSNModal } from "./ChangeSNModal";
import { ActivateMachineModal } from "./ActivateMachineModal";
import { CustomModal } from "../../../components/CustomModal";


export interface MachineEditProps {
    machineId: number;
    canEdit: boolean;
    canTransfer: boolean;
    canAssignToLocation: boolean;
    canActivate: boolean;
    onEdit: () => void;
    onClose: () => void;
}

export const MachineEdit = ({machineId, canEdit, canTransfer, canAssignToLocation, canActivate, onEdit, onClose}: MachineEditProps) => {
    const [machine, setMachine] = useState<any|null>(null);
    const [name, setName] = useState('');
    const [locationId, setLocationId] = useState(-999);
    const [locations, setLocations] = useState<any[]|null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [canAdvancedSettings, setCanAdvancedSettings] = useState(false);
    const [changed, setChanged] = useState(false);
    const [transfer, setTransfer] = useState(false);
    const [changeSN, setChangeSN] = useState(false);
    const [reset, setReset] = useState(false);
    const [advancedSettings, setAdvancedSettings] = useState(false);
    const [activate, setActivate] = useState(false);
    const { t } = useTranslation();

    const canReset = useMemo(() => canActivate && machine?.expireAt && new Date(machine.expireAt).getTime() > Date.now(), [canAdvancedSettings, machine]);

    const fetch = useCallback(() => {
        setError('');
        setChanged(false);
        setLocations(null);
        setLocationId(-999);

        setLoading(true);
        API.GET('/api/admmachines/'+machineId)
            .then(m => {
                setMachine(m);
                setName(m.name);
                setLocationId(m.location ? m.location.id : -999);
                if (canAssignToLocation) API.GET('/api/admlocations')
                    .then(l => setLocations(l))
                    .catch(err => setError(err.message))
                    .finally(() => setLoading(false));
                else { setLoading(false); setLocations([]); }
            })
            .catch(err => { setError(err.message); setLoading(false); });
    }, [machineId, canAssignToLocation]);

    const update = useCallback(() => {
        setLoading(true);
        API.PATCH('/api/admmachines/'+machineId, {
            name: machine.name,
            info: machine.info,
            locationId: locationId === -999 ? null : locationId
        })
            .then(() => { fetch(); onEdit(); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [machineId, machine, locationId]);

    const confirmReset = useCallback(() => {
        API.POST(`/api/admmachines/${machineId}/reset`)
            .catch(err => {
                alert(err.error ?? err.message);
            });
    }, [machine]);

    useEffect(() => {
        setCanAdvancedSettings(machine && machine.advanced_params && machine.advanced_params.length > 0);
    }, [machine]);


    useEffect(() => {
        fetch();
    }, [machineId, canAssignToLocation]);

    if (!machine || !locations) return null;

    return (
        <div className="d-flex flex-column overflow-hidden card">
            <TransferModal machineId={machineId} open={transfer} onClose={() => setTransfer(false)} onShared={() => { setTransfer(false); onEdit(); onClose(); }}/>
            <ChangeSNModal machineId={machineId} open={changeSN} onClose={() => setChangeSN(false)} onDone={() => setChangeSN(false)}/>
            <AdvancedSettingsModal open={advancedSettings} machineId={machineId} onClose={() => setAdvancedSettings(false)}/>
            <ActivateMachineModal open={activate} machineId={machineId} onClose={() => setActivate(false)}/>
            <CustomModal
                open={reset}
                onClose={() => setReset(false)}
                title={t('reset_machine')}
                footer={(
                    <>
                        <MDBBtn color="secondary" onClick={onClose}>{t('cancel')}</MDBBtn>
                        <MDBBtn color="danger" onClick={confirmReset}>{t('confirm')}</MDBBtn>
                    </>
                )}
            >
                <span>{t('reset_machine_prompt')}</span>
            </CustomModal>


            <button type="button" className="btn-close position-absolute" style={{right: 5, top: 5}} onClick={() => onClose ? onClose() : {}}></button>
            <div className="d-flex flex-column overflow-hidden card-body">
                <div className="d-flex flex-column w-100 justify-content-center overflow-hidden">
                    <i className="fas fa-server fa-4x mb-2"/>
                    
                    {loading && (
                        <div className="d-flex w-100 justify-content-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">{t('loading...')}</span>
                            </div>
                        </div>
                        
                    )}
                    {!loading && <>
                        <h5 className="text-center">{name}</h5>
                        
                        <div className="d-flex flex-column overflow-auto pe-1 pt-2">
                            { !canEdit && <>
                                <ul className="list-group list-group-light p-0">
                                    <li className="list-group-item p-1"><i className="fas fa-circle-info fa-fw me-1"/><span className="fw-bold me-1">{t('info')}:</span>{machine.info}</li>
                                </ul>
                            </> }

                            { canEdit && <>
                                <TextInput label={t('name')} icon="pen-to-square" value={machine.name} onValueChange={name => { setMachine((m: any) => ({...m, name})); setChanged(true); }}/>
                                <TextAreaInput label={t('info')} icon="circle-info" rows={3} value={machine.info ?? ''} onValueChange={info => { setMachine((m: any) => ({...m, info})); setChanged(true); }}/>
                            </> }

                            { canAssignToLocation && <div className="d-flex mb-3 flex-row align-items-center">
                                <i className="fas fa-map-marker-alt fa-fw"/>
                                <div className="col ms-2">
                                    <CustomSelect title={t('location')}
                                        items={[{val: -999, title: t('unassigned'), icon: 'fas fa-link-slash'}].concat(locations.filter(l => l.lType !== 1).map(l => ({val: l.id, title: l.Name, icon: ''})))}
                                        selected={locationId}
                                        w100
                                        onSelect={id => { setLocationId(id as number); setChanged(true); } }
                                    />
                                </div>
                            </div> }

                            <Alert type="danger" message={error}/>

                            { (canEdit || canAssignToLocation) && <button className="btn btn-primary mb-2" disabled={!changed || loading} onClick={() => update()}>{t('save')}</button> }
                            { canTransfer && <button className="btn btn-secondary mb-2" onClick={() => setTransfer(true)}>{t('transfer')}</button> }
                            { canEdit && <MDBBtn color="secondary" className="mb-2" onClick={() => setChangeSN(true)}>{t('machine:changeSN')}</MDBBtn>}
                            { canAdvancedSettings && <button className="btn btn-secondary" onClick={() => setAdvancedSettings(machine.id)}>{t('advanced_settings')}</button>}
                            { (canActivate && machine.expireAt == null) && <MDBBtn color="secondary" className="mb-2" onClick={() => setActivate(true)}>{t('machine:activate')}</MDBBtn>}
                            { canReset && <MDBBtn color="secondary" className="mb-2" onClick={() => setReset(true)}>{t('machine:reset')}</MDBBtn>}
                        </div>
                        

                    </>}
                </div>
            </div>
        </div>
    )
}